/* ----------------------------------------------------------------------------
 * Social Connections — pure view/model layer (no network, no import.meta).
 *
 * DEPENDENCY-FREE by design so it can be unit-tested under
 * `node --test --experimental-strip-types` exactly like data/connections-model.ts
 * and data/active-domain.ts (the repo's no-jsdom convention: test the pure model
 * the thin React view renders, not the DOM). The fetch layer lives beside it in
 * data/sources-connections.ts (which imports lib/api).
 *
 * Contract source of truth (LOCKED — Lane B backend, already implemented):
 *   modules/social/Http/SocialConnectionsRoutes.php
 *     GET  /v1/social/connections                     -> { ok, connections:[...] }
 *     POST /v1/social/connections/{id}/assign-domain  -> { ok, id, provider,
 *                                                          project_id, persisted }
 *   modules/social/Repositories/SocialConnectionRepository.php::listForUi()
 *     (secret-free projection: id, provider, external_account_ref, display_name,
 *      project_id, status, connected_at)
 *
 * HONESTY INVARIANTS:
 *  - project_id null  => the account is shown as "Unassigned" (never faked).
 *  - persisted:false  => an HONEST no-op that happens BEFORE the domain-scoping
 *    migration lands. The UI keeps the account "Unassigned" and shows a subtle
 *    "assignment will take effect after the pending update" hint — NEVER a false
 *    success. See assignmentView() + the pending-hint locks in the tests.
 * -------------------------------------------------------------------------- */

// ---- Endpoints (the ONLY social-connection endpoints this lane is authorised to use) ----
// Paths are relative to the API base ("/v1"); lib/api prefixes the origin.
export const SOCIAL_CONNECTION_ENDPOINTS = {
  /** GET — list this workspace's social connections (session-scoped, no CSRF). */
  list: '/social/connections',
  /** POST — assign (project_id>0) or clear (null) the SEO domain a connection
   *  belongs to. Session + CSRF double-submit (attached automatically by lib/api). */
  assignDomain: (id: number | string): string => `/social/connections/${id}/assign-domain`,
} as const;

// ---- Wire shapes ------------------------------------------------------------

/** One social connection (GET /v1/social/connections item). Carries NO secret. */
export interface SocialConnectionDto {
  id: number;
  provider: string; // 'facebook' | 'instagram' | ...
  external_account_ref: string;
  display_name: string;
  project_id: number | null; // null => Unassigned (server-authoritative)
  status: string;
  connected_at: string;
}

/** POST /v1/social/connections/{id}/assign-domain response. */
export interface AssignDomainResult {
  ok: boolean;
  id: number;
  provider: string;
  project_id: number | null;
  /** false when the gated domain-scoping migration has not landed yet (honest no-op). */
  persisted: boolean;
}

// ---- provider labelling / grouping -----------------------------------------

/** Known providers get a proper brand label; anything else is title-cased so an
 *  unrecognised provider still renders honestly (never blank, never invented). */
const PROVIDER_LABELS: Record<string, string> = {
  facebook: 'Facebook',
  instagram: 'Instagram',
  linkedin: 'LinkedIn',
  x: 'X',
  twitter: 'X',
  tiktok: 'TikTok',
  youtube: 'YouTube',
  pinterest: 'Pinterest',
  threads: 'Threads',
};

export function providerLabel(provider: string): string {
  const key = (provider ?? '').trim().toLowerCase();
  if (key === '') return 'Unknown provider';
  return PROVIDER_LABELS[key] ?? key.charAt(0).toUpperCase() + key.slice(1);
}

// ---- connection row model ---------------------------------------------------

export type StatusTone = 'success' | 'neutral' | 'warning';

export interface SocialConnectionRow {
  id: number;
  provider: string;
  providerLabel: string;
  /** the human account name; falls back to the external ref, then a stable id. */
  label: string;
  /** the raw external account reference (handle / page id). */
  accountRef: string;
  statusLabel: string;
  statusTone: StatusTone;
  connectedLabel: string;
  /** assigned SEO domain id, or null when unassigned. */
  projectId: number | null;
}

/** Coerce a wire project_id to a positive int or null (0/''/negative -> null). */
export function normalizeProjectId(v: number | null | undefined): number | null {
  if (v === null || v === undefined) return null;
  const n = Number(v);
  return Number.isFinite(n) && n > 0 ? Math.trunc(n) : null;
}

/** Deterministic UTC date label (locale-independent for tests): "2026-07-21". */
export function formatConnectedDate(iso: string | null | undefined): string {
  if (!iso) return '—';
  const t = Date.parse(iso);
  if (Number.isNaN(t)) return '—';
  return new Date(t).toISOString().slice(0, 10);
}

/** Map a wire DTO to exactly the fields the row renders. Only known, safe fields
 *  are read — the projection carries no secret, and none is ever synthesised. */
export function socialConnectionRow(dto: SocialConnectionDto): SocialConnectionRow {
  const status = (dto.status ?? '').toLowerCase();
  const statusTone: StatusTone =
    status === 'connected' || status === 'active' ? 'success'
      : status === 'error' ? 'warning'
      : 'neutral';
  const statusLabel =
    status === 'connected' || status === 'active' ? 'Connected'
      : status === 'error' ? 'Error'
      : status === '' ? 'Unknown'
      : status.charAt(0).toUpperCase() + status.slice(1);
  return {
    id: dto.id,
    provider: (dto.provider ?? '').toLowerCase(),
    providerLabel: providerLabel(dto.provider),
    label: (dto.display_name ?? '').trim() || (dto.external_account_ref ?? '').trim() || `Account ${dto.id}`,
    accountRef: (dto.external_account_ref ?? '').trim(),
    statusLabel,
    statusTone,
    connectedLabel: formatConnectedDate(dto.connected_at),
    projectId: normalizeProjectId(dto.project_id),
  };
}

// ---- provider grouping (the page lists accounts grouped by provider) --------

export interface ProviderGroup {
  provider: string;
  providerLabel: string;
  rows: SocialConnectionRow[];
}

/** Group rows by provider, preserving first-seen order (list is id-DESC from the
 *  server), each group's rows in their arrival order. Stable + deterministic. */
export function groupByProvider(rows: readonly SocialConnectionRow[]): ProviderGroup[] {
  const order: string[] = [];
  const byProvider = new Map<string, ProviderGroup>();
  for (const row of rows) {
    let group = byProvider.get(row.provider);
    if (!group) {
      group = { provider: row.provider, providerLabel: row.providerLabel, rows: [] };
      byProvider.set(row.provider, group);
      order.push(row.provider);
    }
    group.rows.push(row);
  }
  return order.map((p) => byProvider.get(p)!);
}

// ---- domain (per-account) association ---------------------------------------

/** Minimal domain shape the page passes (a subset of useActiveDomain's DomainOption). */
export interface DomainChoiceOption {
  projectId: number;
  hostname: string;
  displayName?: string;
}

/** The sentinel value the row Domain dropdown uses for "no domain". */
export const UNASSIGNED_DOMAIN_VALUE = '' as const;

/** Human label for an account's assigned domain: "Unassigned" when null, the
 *  domain's "display · host" (or host) when known, or a stable fallback for an id
 *  not in the caller's list (e.g. a domain removed from the workspace). */
export function connectionDomainLabel(
  projectId: number | null,
  domains: readonly DomainChoiceOption[],
): string {
  if (projectId == null) return 'Unassigned';
  const d = domains.find((x) => x.projectId === projectId);
  if (!d) return `Domain #${projectId}`;
  const name = (d.displayName ?? '').trim();
  return name && name !== d.hostname ? `${name} · ${d.hostname}` : d.hostname;
}

// ---- assignment outcome (persisted:false honesty) ---------------------------

export interface AssignmentView {
  /** What the account's assigned domain reads as AFTER this call. Pre-migration
   *  (persisted:false) this stays the PREVIOUS value (i.e. still Unassigned) —
   *  the no-op is never rendered as a successful assignment. */
  effectiveProjectId: number | null;
  /** true only on a genuine, persisted assignment (drives a success reflection). */
  persisted: boolean;
  /** true when the write was a pre-migration no-op — show the pending hint. */
  pending: boolean;
}

/** Subtle, honest copy shown when an assign call returned persisted:false. */
export const PENDING_ASSIGNMENT_HINT =
  'Saved — this domain assignment will take effect after the pending update.';

/**
 * Interpret an assign-domain result honestly.
 *  - persisted:true  -> the server persisted the choice; reflect result.project_id.
 *  - persisted:false -> an HONEST no-op (migration not yet landed); keep the
 *    account on its PREVIOUS domain (Unassigned) and mark it pending so the view
 *    shows the pending hint instead of claiming success.
 */
export function assignmentView(
  previousProjectId: number | null,
  result: AssignDomainResult,
): AssignmentView {
  if (result.persisted) {
    return { effectiveProjectId: normalizeProjectId(result.project_id), persisted: true, pending: false };
  }
  return { effectiveProjectId: previousProjectId, persisted: false, pending: true };
}

/** The exact POST body the assign-domain call sends. Isolated as a PURE helper
 *  so the request-body contract is node-testable without importing the fetch
 *  layer (data/sources-connections.ts pulls in @/lib/api + import.meta, which
 *  cannot resolve under `node --test` — the repo convention, see voice/net). */
export function assignDomainBody(projectId: number | null): { project_id: number | null } {
  return { project_id: projectId };
}

// ---- error copy (pure; operates on {status,code} so it stays node-testable) --

/** Structural view of lib/api's ApiError (status + code) without importing it
 *  (importing lib/api would pull in import.meta and break node --test). ApiError
 *  is structurally assignable to this. */
export interface ApiErrorLike {
  status?: number;
  code?: string;
}

/** Narrow an unknown thrown value (React Query surfaces `unknown`/`Error`) to the
 *  {status,code} shape the copy helpers read. Non-ApiError values yield {} so the
 *  helpers fall through to their generic message. */
function asApiErrorLike(err: unknown): ApiErrorLike {
  if (err && typeof err === 'object') {
    const e = err as Record<string, unknown>;
    return {
      status: typeof e.status === 'number' ? e.status : undefined,
      code: typeof e.code === 'string' ? e.code : undefined,
    };
  }
  return {};
}

/** Honest user-facing copy for a failed LIST. Distinguishes the 503 disabled
 *  state, the auth/permission states, and the network/generic fallbacks. */
export function listConnectionsErrorMessage(err: unknown): string {
  const { code, status } = asApiErrorLike(err);
  if (code === 'network_error' || status === 0)
    return 'Could not reach BookedCentral. Check your connection and try again.';
  if (code === 'social_meta_disabled' || status === 503)
    return 'Social connections aren’t enabled for your workspace yet.';
  if (code === 'not_authenticated' || status === 401)
    return 'Your session has expired. Sign in again to view your connections.';
  if (status === 403) return 'Your role can’t view social connections. Ask a workspace owner.';
  return 'Could not load your social connections. Please try again.';
}

/** True when the list failure is specifically the connector-disabled (503) state,
 *  so the page can render a calm "not enabled yet" panel instead of an error. */
export function isSocialDisabled(err: unknown): boolean {
  const { code, status } = asApiErrorLike(err);
  return code === 'social_meta_disabled' || status === 503;
}

/** Honest user-facing copy for a failed ASSIGN. */
export function assignDomainErrorMessage(err: unknown): string {
  const { code, status } = asApiErrorLike(err);
  if (code === 'network_error' || status === 0)
    return 'Could not reach BookedCentral. The domain was not changed — try again.';
  if (code === 'social_meta_disabled' || status === 503)
    return 'Social connections aren’t enabled for your workspace yet.';
  if (code === 'csrf_failed' || status === 403)
    return 'Your session needs refreshing. Reload the page and try again.';
  if (code === 'domain_not_found') return 'That domain isn’t in your workspace anymore.';
  if (code === 'not_found' || status === 404) return 'That connection no longer exists.';
  if (code === 'invalid_project_id' || code === 'invalid_body' || status === 400)
    return 'That domain selection wasn’t valid. Pick a domain and try again.';
  if (code === 'not_authenticated' || status === 401)
    return 'Your session has expired. Sign in again.';
  return 'Could not save the domain. Please try again.';
}
